<template>
  <v-card :class="cropperOpen ? '' : 'hide'" elevation="0" class="cropper-card">
    <!-- GIF processing overlay -->
    <v-overlay
      :model-value="gifProcessing"
      contained
      class="d-flex align-center justify-center"
    >
      <div class="gif-loading d-flex flex-column align-center ga-3">
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
          width="4"
        />
        <span class="gif-loading-text">Processing GIF…</span>
      </div>
    </v-overlay>

    <!-- Cropper canvas -->
    <VuePictureCropper
      :style="{
        maxHeight: '600px',
        width: 'auto',
        backgroundColor: noBackground ? 'transparent' : color,
      }"
      ref="cropper"
      :src="media.url"
      :aspectRatio="aspectRatio || 1200 / 850"
      :autoCropArea="1"
      :zoomOnWheel="false"
      :background="noBackground"
      :modal="false"
    />

    <!-- Floating color picker (anchored popover) -->

    <!-- Floating pill toolbar — overlaid on the canvas -->
    <div class="crop-toolbar">
      <div class="toolbar-pill d-flex align-center">
        <!-- Rotate -->
        <v-btn
          icon
          size="small"
          variant="text"
          class="tool-btn"
          @click="rotateLeft"
        >
          <v-icon size="19">mdi-rotate-left</v-icon>
        </v-btn>
        <v-btn
          icon
          size="small"
          variant="text"
          class="tool-btn"
          @click="rotateRight"
        >
          <v-icon size="19">mdi-rotate-right</v-icon>
        </v-btn>

        <div class="tool-sep" />

        <!-- Zoom -->
        <v-btn
          icon
          size="small"
          variant="text"
          class="tool-btn"
          @click="zoomIn"
        >
          <v-icon size="19">mdi-magnify-plus-outline</v-icon>
        </v-btn>
        <v-btn
          icon
          size="small"
          variant="text"
          class="tool-btn"
          @click="zoomOut"
        >
          <v-icon size="19">mdi-magnify-minus-outline</v-icon>
        </v-btn>

        <div class="tool-sep" />

        <!-- Background fill -->
        <v-tooltip
          :text="
            noBackground
              ? $t('gallery.showBackgroundColor')
              : $t('gallery.hideBackgroundColor')
          "
          location="top"
        >
          <template #activator="{ props: tp }">
            <v-btn
              v-bind="tp"
              icon
              size="small"
              variant="text"
              class="tool-btn"
              :class="{ 'tool-btn--on': !noBackground }"
              @click="noBackground = !noBackground"
            >
              <v-icon size="19">mdi-image-filter-black-white</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-menu
          v-if="!noBackground"
          v-model="openColorPicker"
          :close-on-content-click="false"
          location="top center"
          origin="bottom center"
          offset="10"
          transition="scale-transition"
        >
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="color-dot"
              :style="{ background: color }"
            />
          </template>
          <v-color-picker
            :model-value="color"
            @update:model-value="color = $event"
            hide-inputs
            dot-size="25"
            swatches-max-height="200"
            class="color-picker-float"
          />
        </v-menu>

        <div class="tool-sep" />

        <!-- Close -->
        <v-btn
          icon
          size="small"
          variant="text"
          class="tool-btn close-btn"
          @click="cropperOpen = false"
        >
          <v-icon size="19">mdi-close</v-icon>
        </v-btn>

        <!-- Apply -->
        <v-btn
          class="apply-btn"
          size="small"
          rounded="pill"
          :loading="gifProcessing"
          @click="confirmCrop"
        >
          <v-icon size="15" class="me-1">mdi-check</v-icon>
          {{ $t('generics.confirm') }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import VuePictureCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { cropAnimatedGif, isAnimatedGif } from '@/helpers/gifCropper'

const props = defineProps({
  cropped: Boolean,
  media: { type: Object, required: true },
  aspectRatio: Number,
  modelValue: Boolean,
})
const color = ref('#000000')
const openColorPicker = ref(false)
const noBackground = ref(true)
const isGif = ref(false)
const gifProcessing = ref(false)

const emit = defineEmits([
  'update:modelValue',
  'crop-confirmed',
  'cropper-mounted',
])

const cropper = ref(null)

const cropperOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

// Detect animated GIF on mount / when media changes
watch(
  () => props.media?.url,
  async url => {
    if (url && props.media?.mime?.includes('gif')) {
      isGif.value = await isAnimatedGif(url)
    } else {
      isGif.value = false
    }
  },
  { immediate: true }
)

function zoomIn() {
  cropper.value?.relativeZoom(0.1)
}
function zoomOut() {
  cropper.value?.relativeZoom(-0.1)
}
function rotateLeft() {
  cropper.value?.rotate(-90)
}
function rotateRight() {
  cropper.value?.rotate(90)
}

async function confirmCrop() {
  const cropperInstance = cropper.value
  if (!cropperInstance) return

  // If this is an animated GIF, use the special GIF cropper
  if (isGif.value) {
    return confirmGifCrop(cropperInstance)
  }

  const cropperData = {
    fillColor: noBackground.value ? undefined : color.value,
    width: 1200,
    height: 850,
  }
  const canvas = await cropperInstance.getCroppedCanvas(cropperData)
  const mimeType = noBackground.value ? 'image/png' : 'image/jpeg'
  const dataUrl = canvas.toDataURL(mimeType)
  const croppedBlob = await new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(blob)
    }, mimeType)
  })
  const blobSizeInBytes = croppedBlob.size
  const blobSizeInMB = blobSizeInBytes / (1024 * 1024)
  console.log(blobSizeInMB)

  emit('crop-confirmed', {
    file: croppedBlob,
    url: dataUrl,
    name: props.media.name,
    mime: mimeType,
    fillColor: noBackground.value ? false : color.value,
  })

  cropperOpen.value = false
}

async function confirmGifCrop(cropperInstance) {
  try {
    gifProcessing.value = true

    // Get crop box data relative to the *natural* image
    const cropBoxData = cropperInstance.getData(true) // rounded, in natural pixels

    const cropRegion = {
      x: cropBoxData.x,
      y: cropBoxData.y,
      width: cropBoxData.width,
      height: cropBoxData.height,
    }

    const { blob, dataUrl } = await cropAnimatedGif(
      props.media.url,
      cropRegion,
      {
        outputWidth: 1200,
        outputHeight: 850,
      }
    )

    const blobSizeInMB = blob.size / (1024 * 1024)
    console.log('GIF size:', blobSizeInMB, 'MB')

    emit('crop-confirmed', {
      file: blob,
      url: dataUrl,
      name: props.media.name,
      mime: 'image/gif',
      fillColor: false,
    })

    cropperOpen.value = false
  } catch (err) {
    console.error('GIF crop failed:', err)
  } finally {
    gifProcessing.value = false
  }
}

function onEnter(e) {
  if (e.key === 'Enter') confirmCrop()
}

onMounted(() => {
  if (props.media.fillColor) {
    color.value = props.media.fillColor
    noBackground.value = false
  }
  emit('cropper-mounted', cropper.value)
  window.addEventListener('keyup', onEnter)
})

onBeforeUnmount(() => {
  window.removeEventListener('keyup', onEnter)
})
</script>

<style lang="scss">
// ── Card & cropper overrides ──────────────────────────────────────────────────
.cropper-card {
  padding: 8px !important;
  font-family: 'Fredoka', sans-serif;
  position: relative;
  height: max-content;
  .hide {
    display: none !important;
  }

  .cropper-container {
    background-color: v-bind("noBackground ? 'transparent' : color") !important;
  }
  .cropper-view-box {
    outline-color: v-bind(
      "noBackground ? 'rgba(55, 174, 239, 0.75)' : color"
    ) !important;
  }
}

// ── Floating toolbar ─────────────────────────────────────────────────────────
.crop-toolbar {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
}

.toolbar-pill {
  pointer-events: all;
  background: rgba(14, 16, 24, 0.8);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 5px 10px;
  gap: 2px;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.35);
}

.tool-sep {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.14);
  border-radius: 1px;
  margin: 0 6px;
  flex-shrink: 0;
}

.tool-btn {
  color: rgba(255, 255, 255, 0.7) !important;
  border-radius: 8px !important;
  width: 32px !important;
  height: 32px !important;
  transition:
    background 0.15s,
    color 0.15s !important;

  &:hover {
    background: rgba(255, 255, 255, 0.12) !important;
    color: #ffffff !important;
  }

  &--on {
    background: rgba(33, 150, 243, 0.28) !important;
    color: #85caff !important;
  }
}

.close-btn:hover {
  background: rgba(255, 82, 82, 0.2) !important;
  color: #ff8a80 !important;
}

// ── Color dot ────────────────────────────────────────────────────────────────
.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  cursor: pointer;
  flex-shrink: 0;
  margin: 0 4px;
  transition:
    transform 0.15s ease,
    border-color 0.15s ease;
  outline: none;

  &:hover {
    border-color: #fff;
    transform: scale(1.18);
  }
}

// ── Apply button ─────────────────────────────────────────────────────────────
.apply-btn {
  background: linear-gradient(99.39deg, #d973fe 0%, #4a72ff 98.23%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px;
  box-shadow: 0px 2px 0px 0px rgba(47, 52, 203, 0.65) !important;
  padding: 0 14px !important;
  height: 32px !important;
  margin-inline-start: 4px;
  transition:
    box-shadow 0.12s,
    transform 0.12s !important;

  &:active {
    box-shadow: none !important;
    transform: translateY(1px);
  }
}

// ── Color picker popover ────────────────────────────────────────────────────
.color-picker-float {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22) !important;
}

// ── GIF loading ──────────────────────────────────────────────────────────────
.gif-loading {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

.gif-loading-text {
  font-family: 'Fredoka', sans-serif;
  font-size: 14px;
  color: #5a6a7a;
  font-weight: 500;
}
</style>
